<script lang="ts">
  // CubeEditor wraps CubeTable with click-to-stage behaviour and the
  // staged-edits dialog. A click on a cell pops up a tiny inline form
  // that stages the new rate / max amount; the staged value renders
  // in-cell as "before → after". The store debounces auto-save through
  // the proxy Worker; the "Saved Ns ago" indicator reflects what came
  // back from saveDraft. Submit for Review opens the §7 form
  // (SubmitForReview); on success the proposal is created and the
  // editor clears.

  import type { ProductCatalogueFile, CubeCellValue, EstablishmentFeeOverride, EstablishmentFeeOverrideTier, ProposeResponse } from '../lib/types';
  import { stagedEditsState, stageCell, unstageCell, stageFeeOverrideEdit, unstageFeeOverrideEdit } from '../lib/staged-edits.svelte';
  import { applyStagedEditsToCatalogue, bureauBandWarnings } from '../lib/staged-edits';
  import CubeTable from './CubeTable.svelte';
  import SubmitForReview from './SubmitForReview.svelte';
  import ValueTypeLockBanner from './ValueTypeLockBanner.svelte';
  import { formatRate, formatAmount, relativeTimeAgo } from '../lib/formatters';
  import { navigate } from '../lib/router.svelte';

  interface Props {
    catalogue: ProductCatalogueFile;
    channel: string;
  }

  const { catalogue, channel }: Props = $props();

  let editingCoord = $state<string | null>(null);
  let editingBefore = $state<CubeCellValue | null>(null);
  // Svelte 5's `bind:value` on `type="number"` coerces the bound
  // variable to a number (or null for an empty input) regardless of the
  // declared type. Reflect that here rather than fighting it —
  // commitInline reads the value and handles string / number / null
  // uniformly. Without this, `(raw as string).trim()` throws once the
  // user types anything and the stage never lands.
  let inputRate = $state<number | string | null>('');
  let inputAmount = $state<number | string | null>('');
  let rateInputEl: HTMLInputElement | null = $state(null);

  // When the inline editor opens, focus and select the rate input so
  // typing replaces the existing value cleanly. Without this, the user
  // had to manually click-then-select; clicking the amount input by
  // mistake staged an amount edit instead of the rate edit they
  // intended.
  $effect(() => {
    if (editingCoord && rateInputEl) {
      rateInputEl.focus();
      rateInputEl.select();
    }
  });

  // When the value-type lock fires while a cell editor or override
  // editor is open, close the editor so the operator does not see a
  // dimmed-but-still-visible editor stranded by the §1.4 one-draft-
  // one-value-type rule. The §1.4 lock itself is per-spec; this effect
  // is the UX completion that keeps the lock state unambiguous on
  // screen. Watching `locked` / `overrideLocked` rather than the value-
  // type directly so the close fires the instant the lock transitions
  // — for example, immediately after the user commits an override edit
  // that flips valueType from null to 'fees'.
  $effect(() => {
    if (locked && editingCoord) {
      editingCoord = null;
      editingBefore = null;
    }
  });
  $effect(() => {
    if (overrideLocked && editingOverrideId) {
      editingOverrideId = null;
      editingOverrideBefore = null;
      editingOverrideTiers = [];
      overrideError = null;
    }
  });

  // Only forward the fields that were actually staged — NOT a merge of
  // before + staged. The CubeTable uses `undefined` on a per-field basis
  // to decide whether to render the "before → after" arrow preview, so
  // if the user stages an amount edit only, the rate cell must continue
  // to read `interest_rate_pa === undefined` (no arrow), not the before
  // value (which would render misleadingly as "5.76 → 5.76").
  const stagedByCoordinate = $derived(
    Object.fromEntries(
      Object.entries(stagedEditsState.edits).map(([coord, edit]) => [
        coord,
        edit.staged
      ])
    )
  );

  const candidateCube = $derived(
    Object.keys(stagedEditsState.edits).length > 0
      ? applyStagedEditsToCatalogue(catalogue, stagedEditsState.edits)
      : null
  );

  const monotonicityWarnings = $derived(
    bureauBandWarnings(catalogue, stagedEditsState.edits, channel)
  );

  const saveLabel = $derived(saveLabelFor(stagedEditsState.save));

  function saveLabelFor(save: typeof stagedEditsState.save): string {
    if (save.state === 'saving') return 'Saving…';
    if (save.state === 'pending') return 'Saving (queued)…';
    if (save.state === 'error') return `Save failed (${save.errorMessage ?? 'unknown'})`;
    if (save.state === 'saved' && save.lastSavedAt) return `Saved ${relativeTimeAgo(save.lastSavedAt)}`;
    return Object.keys(stagedEditsState.edits).length > 0 ? 'Pending save' : 'No edits yet';
  }

  function handleCellClick(coordinates: string, before: CubeCellValue) {
    if (locked) return;
    editingCoord = coordinates;
    editingBefore = before;
    const staged = stagedEditsState.edits[coordinates]?.staged;
    inputRate = staged?.interest_rate_pa ?? before.interest_rate_pa ?? null;
    inputAmount = staged?.max_loan_amount ?? before.max_loan_amount ?? null;
  }

  function commitInline(field: 'interest_rate_pa' | 'max_loan_amount') {
    if (!editingCoord || !editingBefore) return;
    const raw = field === 'interest_rate_pa' ? inputRate : inputAmount;
    if (raw === null || raw === undefined || raw === '') return;
    const value = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (!Number.isFinite(value)) return;
    if (value === editingBefore[field]) return;
    stageCell(editingCoord, editingBefore, field, value);
  }

  function closeEditor() {
    editingCoord = null;
    editingBefore = null;
  }

  function removeStaged(coord: string) {
    unstageCell(coord);
  }

  function staged(coord: string) {
    return stagedEditsState.edits[coord];
  }

  let submitOpen = $state<boolean>(false);

  // Lock when the store holds edits of a different value type (a
  // non-rates draft is in flight). The cube editor blocks new
  // cell-clicks until the operator submits or discards the other
  // category. Reactive on store state.
  const locked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'rates'
  );

  // Establishment-fee override edits sit in the `fees` value-type
  // bucket (GOVERNANCE-ARCHITECTURE.md §9.1). Lock the override
  // affordances when the store holds edits of a different value type.
  const overrideLocked = $derived(
    stagedEditsState.valueType !== null && stagedEditsState.valueType !== 'fees'
  );

  // Surfaces the staged override edits for the CubeTable's sub-line
  // renderer. A key with value `null` means "staged for removal"; a key
  // with an object value means "staged set"; an absent key means
  // "untouched, render the catalogue's current override".
  const stagedOverrideByCreditRating = $derived(
    Object.fromEntries(
      Object.entries(stagedEditsState.fee_override_edits).map(([id, edit]) => [id, edit.staged])
    )
  );

  // Override-editor working state. Each tier carries string fields so
  // empty inputs do not coerce to NaN; commitOverrideEdit converts on
  // save. The B-lite UX renders a single-tier override as a scalar
  // input above the tier table; the table itself is hidden when
  // exactly one tier is staged.
  interface OverrideEditorTier {
    min_amount: string;
    max_amount: string;
    fee: string;
  }
  let editingOverrideId = $state<string | null>(null);
  let editingOverrideBefore = $state<EstablishmentFeeOverride | null>(null);
  let editingOverrideTiers = $state<OverrideEditorTier[]>([]);
  let overrideError = $state<string | null>(null);

  function defaultTier(): OverrideEditorTier {
    const max = catalogue.loan_amount_range?.max ?? 100000;
    return { min_amount: '0', max_amount: String(max), fee: '0' };
  }

  function handleOverrideClick(creditRatingId: string, before: EstablishmentFeeOverride | null) {
    if (overrideLocked) return;
    editingOverrideId = creditRatingId;
    editingOverrideBefore = before;
    overrideError = null;
    // Hydrate from any in-flight staged edit first; fall back to the
    // catalogue's current override; final fallback is a fresh single-
    // tier shorthand covering the full loan-amount range.
    const inflight = stagedEditsState.fee_override_edits[creditRatingId]?.staged;
    const seed = inflight !== undefined ? inflight : before;
    if (seed && Array.isArray(seed.tiers) && seed.tiers.length > 0) {
      editingOverrideTiers = seed.tiers.map((t) => ({
        min_amount: String(t.min_amount),
        max_amount: String(t.max_amount),
        fee: String(t.fee)
      }));
    } else {
      editingOverrideTiers = [defaultTier()];
    }
  }

  function closeOverrideEditor() {
    editingOverrideId = null;
    editingOverrideBefore = null;
    editingOverrideTiers = [];
    overrideError = null;
  }

  function addOverrideTier() {
    const last = editingOverrideTiers[editingOverrideTiers.length - 1];
    const lastMax = last ? Number(last.max_amount) : 0;
    const max = catalogue.loan_amount_range?.max ?? 100000;
    editingOverrideTiers = [
      ...editingOverrideTiers,
      { min_amount: String(Number.isFinite(lastMax) ? lastMax + 1 : 0), max_amount: String(max), fee: '0' }
    ];
  }

  function removeOverrideTier(idx: number) {
    editingOverrideTiers = editingOverrideTiers.filter((_, i) => i !== idx);
    if (editingOverrideTiers.length === 0) {
      editingOverrideTiers = [defaultTier()];
    }
  }

  // Derived tier validation. `errors` block commit; `warnings` surface
  // alongside the editor but do not block. Re-evaluates on every keystroke
  // so the operator sees the consequences of their entries inline.
  //
  // Errors:
  //   - Non-numeric / non-finite values.
  //   - Negative values.
  //   - min > max within a single tier.
  //   - Tier ranges overlap (after sorting by min).
  // Warnings:
  //   - Consecutive tier boundaries are not adjacent integers
  //     (tier[n+1].min !== tier[n].max + 1). Loan amounts in the gap fall
  //     back to the product-level establishment-fee tier.
  const overrideTierValidation = $derived.by(() => {
    const errors: string[] = [];
    const warnings: string[] = [];
    const tiersForCheck: { idx: number; min: number; max: number; fee: number; valid: boolean }[] = [];
    for (let i = 0; i < editingOverrideTiers.length; i += 1) {
      const tier = editingOverrideTiers[i]!;
      const min = Number(tier.min_amount);
      const max = Number(tier.max_amount);
      const fee = Number(tier.fee);
      if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(fee)) {
        errors.push(`Tier ${i + 1}: min, max, and fee must all be numbers.`);
        tiersForCheck.push({ idx: i, min, max, fee, valid: false });
        continue;
      }
      if (min < 0 || max < 0 || fee < 0) {
        errors.push(`Tier ${i + 1}: values must be non-negative.`);
      }
      if (min > max) {
        errors.push(`Tier ${i + 1}: min ($${min}) cannot exceed max ($${max}).`);
      }
      tiersForCheck.push({ idx: i, min, max, fee, valid: min <= max && min >= 0 && max >= 0 && fee >= 0 });
    }
    // Cross-tier overlap + adjacency checks operate on the valid-shaped
    // tiers only, so a malformed entry does not mask the structural issues
    // operators are likely also surfacing simultaneously.
    const sorted = tiersForCheck
      .filter((t) => t.valid)
      .sort((a, b) => a.min - b.min);
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      const curr = sorted[i]!;
      if (curr.min <= prev.max) {
        errors.push(`Tier ${prev.idx + 1} ($${prev.min}–$${prev.max}) overlaps tier ${curr.idx + 1} ($${curr.min}–$${curr.max}).`);
      } else if (curr.min !== prev.max + 1) {
        warnings.push(`Tier boundaries $${prev.max} and $${curr.min} are not adjacent integers — loan amounts $${prev.max + 1}–$${curr.min - 1} fall back to the product-level establishment-fee tier.`);
      }
    }
    return { errors, warnings };
  });

  function commitOverrideEdit() {
    if (!editingOverrideId) return;
    if (overrideTierValidation.errors.length > 0) {
      overrideError = overrideTierValidation.errors[0]!;
      return;
    }
    const parsed: EstablishmentFeeOverrideTier[] = editingOverrideTiers.map((tier) => ({
      min_amount: Number(tier.min_amount),
      max_amount: Number(tier.max_amount),
      fee: Number(tier.fee)
    }));
    const staged: EstablishmentFeeOverride = { tiers: parsed };
    stageFeeOverrideEdit(editingOverrideId, editingOverrideBefore, staged);
    closeOverrideEditor();
  }

  function removeOverrideAtCreditRating() {
    if (!editingOverrideId) return;
    stageFeeOverrideEdit(editingOverrideId, editingOverrideBefore, null);
    closeOverrideEditor();
  }

  function unstageOverride(creditRatingId: string) {
    unstageFeeOverrideEdit(creditRatingId);
  }

  function describeOverride(override: EstablishmentFeeOverride | null): string {
    if (!override || !Array.isArray(override.tiers) || override.tiers.length === 0) return '—';
    if (override.tiers.length === 1) {
      const fee = override.tiers[0]?.fee;
      return typeof fee === 'number' ? formatAmount(fee) : '—';
    }
    return `varies (${override.tiers.length} tiers)`;
  }

  function openSubmit() {
    submitOpen = true;
  }
  function closeSubmit() {
    submitOpen = false;
  }
  function onProposalCreated(response: ProposeResponse) {
    submitOpen = false;
    navigate(`/changes/pending-review/${response.pr_number}`);
  }
</script>

<section class:is-locked={locked}>
  {#if locked && stagedEditsState.valueType}
    <ValueTypeLockBanner
      currentValueType={stagedEditsState.valueType}
      attemptedValueType="rates"
    />
  {/if}

  <p class="edit-hint muted">
    <span class="edit-hint__pencil">✎</span>
    Cells marked with the pencil are editable. Click any <strong>%</strong> or <strong>$</strong> cell to stage a change — staged values save automatically and stay private to you until you submit for review.
  </p>

  <CubeTable
    {catalogue}
    {channel}
    onCellClick={handleCellClick}
    {stagedByCoordinate}
    onOverrideClick={overrideLocked ? undefined : handleOverrideClick}
    {stagedOverrideByCreditRating}
  />

  {#if monotonicityWarnings.length > 0}
    <div class="banner banner-warn">
      <strong>Symmetry warning · bureau-band monotonicity</strong>
      <ul>
        {#each monotonicityWarnings as warning, i (i)}
          <li>{warning}</li>
        {/each}
      </ul>
      <p class="muted">Warnings are surfaced for awareness; the reviewer must acknowledge any intentional asymmetry at submission.</p>
    </div>
  {/if}

  {#if editingCoord}
    <div class="cell-editor banner">
      <header>
        <strong>Editing</strong>
        <code>{editingCoord}</code>
        <button class="btn-link" type="button" onclick={closeEditor} aria-label="Close cell editor">Close</button>
      </header>
      <div class="cell-editor__row">
        <label>
          <span class="muted">Interest rate (%)</span>
          <input
            type="number"
            step="0.01"
            bind:value={inputRate}
            bind:this={rateInputEl}
            onblur={() => commitInline('interest_rate_pa')}
            onkeydown={(e) => { if (e.key === 'Enter') { commitInline('interest_rate_pa'); closeEditor(); } }}
          />
        </label>
        <label>
          <span class="muted">Max loan amount ($)</span>
          <input
            type="number"
            step="1"
            bind:value={inputAmount}
            onblur={() => commitInline('max_loan_amount')}
            onkeydown={(e) => { if (e.key === 'Enter') { commitInline('max_loan_amount'); closeEditor(); } }}
          />
        </label>
      </div>
      <p class="muted">Press Enter or click outside the input to stage. Staged values save automatically.</p>
    </div>
  {/if}
</section>

<!-- The override editor and the staged-changes panel sit outside the
  rates-locked section above so the operator can complete or submit an
  in-flight fees draft without the section-level dim hiding the Submit
  button. The rates-lock now only mutes the cube cells + cell editor +
  monotonicity warnings — affordances that are genuinely unavailable
  while a non-rates draft is open. -->

{#if editingOverrideId}
  <div class="cell-editor banner override-editor">
    <header>
      <strong>Editing establishment-fee override</strong>
      <code>rate_cube.{editingOverrideId}.establishment_fee_override</code>
      <button class="btn-link" type="button" onclick={closeOverrideEditor} aria-label="Close override editor">Close</button>
    </header>
    <p class="muted">
      The override applies to every cube cell under credit-rating <code>{editingOverrideId}</code>.
      Per <code>GOVERNANCE-ARCHITECTURE.md</code> §2.2 / §10.2 the same tier shape covers every housing / channel cell underneath.
      A single tier covering the full loan-amount range renders inline as a scalar dollar figure; additional tiers
      introduce per-loan-amount fee variation within this credit-rating row.
    </p>
    <table class="override-tier-table cube-table">
      <thead>
        <tr>
          <th class="num">Min amount ($)</th>
          <th class="num">Max amount ($)</th>
          <th class="num">Fee ($)</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {#each editingOverrideTiers as tier, idx (idx)}
          <tr>
            <td class="num"><input type="number" step="1" min="0" bind:value={tier.min_amount} /></td>
            <td class="num"><input type="number" step="1" min="0" bind:value={tier.max_amount} /></td>
            <td class="num"><input type="number" step="1" min="0" bind:value={tier.fee} /></td>
            <td>
              {#if editingOverrideTiers.length > 1}
                <button class="btn-link" type="button" onclick={() => removeOverrideTier(idx)}>Remove tier</button>
              {/if}
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
    <div class="override-editor__actions">
      <button class="btn btn-link" type="button" onclick={addOverrideTier}>+ Add tier</button>
      <div class="override-editor__commit">
        {#if editingOverrideBefore}
          <button class="btn btn-link" type="button" onclick={removeOverrideAtCreditRating}>Remove override</button>
        {/if}
        <button class="btn" type="button" onclick={closeOverrideEditor}>Cancel</button>
        <button class="btn btn-primary" type="button" onclick={commitOverrideEdit} disabled={overrideTierValidation.errors.length > 0}>Stage override</button>
      </div>
    </div>
    {#if overrideTierValidation.errors.length > 0}
      <ul class="banner banner-warn override-editor__errors">
        {#each overrideTierValidation.errors as error (error)}
          <li>{error}</li>
        {/each}
      </ul>
    {/if}
    {#if overrideTierValidation.warnings.length > 0}
      <ul class="banner override-editor__warnings">
        {#each overrideTierValidation.warnings as warning (warning)}
          <li>{warning}</li>
        {/each}
      </ul>
    {/if}
    {#if overrideError && overrideTierValidation.errors.length === 0}
      <p class="banner banner-warn">{overrideError}</p>
    {/if}
  </div>
{/if}

<section class="staged-panel">
    <header>
      <h3 class="section-heading">Staged changes</h3>
      <span class="muted save-indicator" data-state={stagedEditsState.save.state}>{saveLabel}</span>
    </header>
    {#if Object.keys(stagedEditsState.edits).length === 0 && Object.keys(stagedEditsState.fee_override_edits).length === 0}
      <p class="muted">No staged edits. Click a cube cell to stage a rate change, or click an <strong>est.fee</strong> sub-line to stage a per-credit-rating override.</p>
    {/if}
    {#if Object.keys(stagedEditsState.edits).length > 0}
      <table class="cube-table">
        <thead>
          <tr>
            <th>Coordinates</th>
            <th class="num">% before</th>
            <th class="num">% after</th>
            <th class="num">$ before</th>
            <th class="num">$ after</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each Object.values(stagedEditsState.edits) as edit (edit.coordinates)}
            <tr>
              <td><code>{edit.coordinates}</code></td>
              <td class="num">{formatRate(edit.before.interest_rate_pa)}</td>
              <td class="num" class:unchanged={edit.staged.interest_rate_pa === undefined}>
                {edit.staged.interest_rate_pa !== undefined
                  ? formatRate(edit.staged.interest_rate_pa)
                  : formatRate(edit.before.interest_rate_pa)}
              </td>
              <td class="num">{formatAmount(edit.before.max_loan_amount)}</td>
              <td class="num" class:unchanged={edit.staged.max_loan_amount === undefined}>
                {edit.staged.max_loan_amount !== undefined
                  ? formatAmount(edit.staged.max_loan_amount)
                  : formatAmount(edit.before.max_loan_amount)}
              </td>
              <td><button class="btn-link" type="button" onclick={() => removeStaged(edit.coordinates)}>Remove</button></td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
    {#if Object.keys(stagedEditsState.fee_override_edits).length > 0}
      <h4 class="staged-panel__subheading">Establishment-fee overrides</h4>
      <table class="cube-table">
        <thead>
          <tr>
            <th>Credit rating</th>
            <th>Before</th>
            <th>After</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {#each Object.values(stagedEditsState.fee_override_edits) as edit (edit.credit_rating_id)}
            <tr>
              <td><code>rate_cube.{edit.credit_rating_id}.establishment_fee_override</code></td>
              <td>{describeOverride(edit.before)}</td>
              <td>{describeOverride(edit.staged)}</td>
              <td><button class="btn-link" type="button" onclick={() => unstageOverride(edit.credit_rating_id)}>Remove</button></td>
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
    {#if Object.keys(stagedEditsState.edits).length > 0 || Object.keys(stagedEditsState.fee_override_edits).length > 0}
      <div class="staged-panel__actions">
        <button class="btn btn-primary" type="button" onclick={openSubmit}>Submit for review →</button>
      </div>
    {/if}
</section>

{#if submitOpen}
  <SubmitForReview
    {catalogue}
    valueType={stagedEditsState.valueType ?? 'rates'}
    onClose={closeSubmit}
    onSubmitted={onProposalCreated}
  />
{/if}

<style>
  /* When another value-type's draft is in flight, mute the cube
     editor surface (except the lock banner above) so the operator's
     eye is drawn to the Discard affordance rather than fiddling
     with disabled cells. */
  section.is-locked > :not(:first-child) {
    opacity: 0.55;
    pointer-events: none;
  }
  .cell-editor {
    margin-top: var(--space-3);
    background: var(--surface-card);
    border-color: var(--surface-line);
  }
  .cell-editor header {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin-bottom: var(--space-2);
  }
  .cell-editor__row {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .cell-editor input {
    width: 12rem;
    padding: var(--space-1) var(--space-2);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
  }
  .staged-panel { margin-top: var(--space-5); }
  .staged-panel header {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
  }
  .save-indicator[data-state="error"] { color: var(--accent-fail); }
  .save-indicator[data-state="saved"] { color: var(--accent-pass); }
  .edit-hint {
    padding: var(--space-2) var(--space-3);
    background: var(--input-interactive-bg);
    border-left: 3px solid var(--input-interactive);
    border-radius: var(--radius-sm);
    margin-bottom: var(--space-3);
    font-size: var(--type-sm);
  }
  .edit-hint__pencil {
    color: var(--input-interactive);
    font-weight: 600;
    margin-right: var(--space-1);
  }
  .staged-panel__actions {
    display: flex;
    justify-content: flex-end;
    margin-top: var(--space-3);
  }
  .staged-panel__subheading {
    margin-top: var(--space-4);
    margin-bottom: var(--space-2);
    font-size: var(--type-sm);
    color: var(--surface-muted);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .override-editor input[type="number"] {
    width: 8rem;
    padding: var(--space-1) var(--space-2);
    font-family: var(--font-mono);
    font-variant-numeric: tabular-nums;
    border: 1px solid var(--surface-line);
    border-radius: var(--radius-sm);
  }
  .override-tier-table {
    margin-top: var(--space-2);
  }
  .override-editor__actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: var(--space-3);
    gap: var(--space-2);
    flex-wrap: wrap;
  }
  .override-editor__commit {
    display: flex;
    gap: var(--space-2);
    align-items: center;
  }
  .override-editor__commit .btn-primary[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .override-editor__errors,
  .override-editor__warnings {
    list-style: disc;
    padding-left: var(--space-4);
    margin: var(--space-2) 0 0 0;
    font-size: var(--type-sm);
  }
  .override-editor__errors {
    color: var(--accent-fail);
  }
  .override-editor__warnings {
    color: var(--accent-warn);
    background: var(--surface-card);
    border-left: 3px solid var(--accent-warn);
  }
  /* Unchanged-field cells echo the before value rather than rendering an
   * em-dash, so the reader doesn't mistake "field not staged" for "field
   * cleared". The muted colour keeps the changed field visually dominant
   * in the row. */
  .unchanged {
    color: var(--surface-muted);
    font-style: italic;
  }
  code { font-family: var(--font-mono); }
</style>
